import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type SecurityDepositConfig } from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

/** Cents -> a plain dollar string for the input ("100000" -> "1000"). */
function centsToDollars(cents: number | null | undefined) {
  if (cents == null || !Number.isFinite(Number(cents))) return ''
  return String(Math.round(Number(cents)) / 100)
}

function formatDollars(cents: number | null | undefined) {
  if (cents == null || !Number.isFinite(Number(cents))) return '—'
  return `$${(Math.round(Number(cents)) / 100).toLocaleString('en-US')}`
}

// Form-only schema: the input is a string a human types in dollars, not the
// integer cents the API takes. Bounds mirror admin-api's own validation
// (MIN/MAX_DEPOSIT_AMOUNT_CENTS) so an out-of-range value is caught here
// rather than coming back as a 400.
const depositFormSchema = z.object({
  amount: z
    .string()
    .refine(v => v.trim() !== '' && Number.isFinite(Number(v)), {
      message: 'Enter an amount between $1 and $10,000.',
    })
    .refine(v => Number(v) >= 1 && Number(v) <= 10000, {
      message: 'Enter an amount between $1 and $10,000.',
    }),
})

type DepositFormValues = z.infer<typeof depositFormSchema>

interface SecurityDepositCardProps {
  propertyId: string
  isActive: boolean
}

/**
 * Per-property security deposit amount.
 *
 * Only the amount lives here. Whether deposits run for this property at all is
 * the `security_deposit` feature flag in the Features card below, and neither
 * switch does anything until the global automation switch is enabled on the
 * backend — until then the system records what it would have done without
 * charging anyone. That ordering is deliberate, so an amount can be set up
 * ahead of turning a property on.
 */
export function SecurityDepositCard({ propertyId, isActive }: SecurityDepositCardProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [isClearing, setIsClearing] = useState(false)

  const { data, isLoading, error, refetch } = useQuery<SecurityDepositConfig>({
    queryKey: ['security-deposit-config', propertyId],
    queryFn: () => api.getSecurityDepositConfig(propertyId),
    staleTime: 30000,
    enabled: !!propertyId && !!isActive,
  })

  const overrideCents = data?.amount_cents
  const resolvedCents = data?.resolved_amount_cents
  const defaultCents = data?.default_amount_cents
  const isOverridden = overrideCents != null

  const form = useForm<DepositFormValues>({
    resolver: zodResolver(depositFormSchema),
    defaultValues: { amount: '' },
  })

  // Seed from the effective amount once per property. Keying on propertyId
  // rather than the query data avoids clobbering an unsaved edit when
  // react-query refetches.
  const seededFor = useRef<string | null>(null)
  useEffect(() => {
    if (data && seededFor.current !== propertyId) {
      form.reset({ amount: centsToDollars(resolvedCents) })
      seededFor.current = propertyId
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, resolvedCents, propertyId])

  const isBusy = form.formState.isSubmitting || isClearing

  const onSubmit = async (values: DepositFormValues) => {
    try {
      const cents = Math.round(Number(values.amount) * 100)
      // Entering exactly the default clears the override instead of pinning a
      // redundant one, so the property keeps following any future change to
      // the default rather than silently freezing at today's value.
      const usingDefault = defaultCents != null && cents === defaultCents
      await api.updateSecurityDepositConfig(propertyId, usingDefault ? null : cents)
      await queryClient.invalidateQueries({ queryKey: ['security-deposit-config', propertyId] })
      form.reset({ amount: centsToDollars(usingDefault ? defaultCents : cents) })
      toast({
        title: 'Security deposit saved',
        description: usingDefault
          ? 'This property uses the default deposit amount.'
          : `Deposit set to ${formatDollars(cents)}.`,
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : undefined
      toast({
        title: 'Failed to save security deposit',
        description: message || 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleUseDefault = async () => {
    setIsClearing(true)
    try {
      await api.updateSecurityDepositConfig(propertyId, null)
      await queryClient.invalidateQueries({ queryKey: ['security-deposit-config', propertyId] })
      form.reset({ amount: centsToDollars(defaultCents) })
      toast({
        title: 'Security deposit saved',
        description: 'This property uses the default deposit amount.',
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : undefined
      toast({
        title: 'Failed to save security deposit',
        description: message || 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsClearing(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Security deposit</CardTitle>
        <CardDescription>
          Amount held on the guest&apos;s card before check-in and released after checkout. Applies
          to every booking except Airbnb, which never shares guest card details. Turn the feature on
          for this property with the &ldquo;Security deposit&rdquo; toggle below.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSkeleton className="h-20 w-full" />
        ) : error ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-3">
              <div className="flex flex-wrap items-end gap-3">
                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="text-xs font-medium text-textMuted">
                        Deposit amount ($)
                      </FormLabel>
                      <FormControl>
                        <input
                          type="number"
                          min="1"
                          max="10000"
                          step="1"
                          disabled={isBusy}
                          className="flex h-8 w-28 rounded-input border border-border bg-surface2 px-2 text-sm text-text transition-all focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" size="sm" disabled={isBusy}>
                  Save
                </Button>
                {isOverridden && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={isBusy}
                    className="text-xs text-textMuted"
                    onClick={handleUseDefault}
                  >
                    Use default
                  </Button>
                )}
              </div>
              <p className="text-xs text-textMuted">
                Effective: {formatDollars(resolvedCents)}
                {isOverridden
                  ? ' (per-property override)'
                  : ` (default ${formatDollars(defaultCents)})`}
              </p>
            </form>
          </Form>
        )}
      </CardContent>
    </Card>
  )
}
